import React, { useState, useRef, useEffect, useCallback } from "react";
import { type MeetingNote, type NoteColor, PRESET_COLORS } from "../types/note";
import { useUiLang } from "@/context/UiLangContext";
import { X, Plus, Trash2, Edit3, Save, Download, Check, ChevronUp, FileText, NotebookPen } from "lucide-react";

interface MeetingNotesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  notes: MeetingNote[];
  onAddNote: (text: string, color: NoteColor, title?: string) => void;
  onDeleteNote: (id: string) => void;
  onEditNote: (id: string, text: string, title?: string, color?: NoteColor) => void;
  onExportNotes: () => void;
}

export const MeetingNotesPanel: React.FC<MeetingNotesPanelProps> = ({
  isOpen,
  onClose,
  notes,
  onAddNote,
  onDeleteNote,
  onEditNote,
  onExportNotes,
}) => {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";

  // Position and dragging state
  const [position, setPosition] = useState({ x: window.innerWidth - 400, y: 80 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Note creation inputs
  const [noteTitle, setNoteTitle] = useState("");
  const [showTitleField, setShowTitleField] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [selectedColor, setSelectedColor] = useState<NoteColor>("#a855f7");

  // Inline editing note state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editColor, setEditColor] = useState<NoteColor>("#3b82f6");

  const panelRef = useRef<HTMLDivElement>(null);

  // Reposition panel if window size changes
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => {
        const maxX = window.innerWidth - 380;
        const maxY = window.innerHeight - 400;
        return {
          x: Math.max(10, Math.min(maxX, prev.x)),
          y: Math.max(60, Math.min(maxY, prev.y)),
        };
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Only drag when clicking the header or drag grip icon
    const target = e.target as HTMLElement;
    if (target.closest(".drag-handle")) {
      setDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging) return;
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      const maxX = window.innerWidth - 380;
      const maxY = window.innerHeight - 200;

      setPosition({
        x: Math.max(10, Math.min(maxX, newX)),
        y: Math.max(60, Math.min(maxY, newY)),
      });
    },
    [dragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  useEffect(() => {
    if (dragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, handleMouseMove, handleMouseUp]);

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    onAddNote(noteText.trim(), selectedColor, noteTitle.trim() || undefined);
    setNoteText("");
    setNoteTitle("");
    setShowTitleField(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const text = e.dataTransfer.getData("text/plain");
    if (text) {
      onAddNote(text, selectedColor, isEn ? "Quick note" : "Nota rápida");
    }
  };

  const handleStartEdit = (note: MeetingNote) => {
    setEditingId(note.id);
    setEditText(note.text);
    setEditTitle(note.title || "");
    setEditColor(note.color);
  };

  const handleSaveEdit = (id: string) => {
    if (!editText.trim()) return;
    onEditNote(id, editText.trim(), editTitle.trim() || undefined, editColor);
    setEditingId(null);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      className="fixed w-[400px] h-[480px] max-h-[85vh] flex flex-col bg-[var(--bg2)] border border-[var(--b2)] rounded-xl shadow-2xl z-50 overflow-hidden font-sans transition-shadow select-none text-[var(--text)]"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      {/* Drag & Drop Indicator Panel Header */}
      <div
        onMouseDown={handleMouseDown}
        className="drag-handle flex items-center justify-between px-3.5 py-2.5 bg-[var(--bg3)] border-b border-[var(--b1)] cursor-move select-none"
      >
        <div className="flex items-center gap-1.5 text-[var(--text)]">
          <NotebookPen size={13} className="text-[var(--text)] stroke-[2.5]" />
          <span className="font-bold text-[11px] tracking-wider uppercase">
            {isEn ? "Meeting Notes" : "Notas de la Reunión"}
          </span>
        </div>
        <div className="flex items-center">
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)] rounded transition-colors cursor-pointer"
            title={isEn ? "Close panel" : "Cerrar panel"}
          >
            <ChevronUp size={14} />
          </button>
        </div>
      </div>

      {/* Add Note Form */}
      <form onSubmit={handleAddSubmit} className="p-3 border-b border-[var(--b1)] flex flex-col gap-2 flex-shrink-0">
        {showTitleField && (
          <div className="relative w-full">
            <input
              type="text"
              placeholder={isEn ? "Title (optional)..." : "Título (opcional)..."}
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              className="w-full text-xs bg-[var(--bg3)] border border-[var(--b1)] rounded-lg px-2.5 py-1.5 pr-7 text-[var(--text)] placeholder-[var(--text-dim)] focus:border-accent outline-none font-semibold"
            />
            <button
              type="button"
              onClick={() => {
                setShowTitleField(false);
                setNoteTitle("");
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-dim)] hover:text-red-400"
            >
              <X size={12} />
            </button>
          </div>
        )}
        <textarea
          placeholder={isEn ? "Write a note..." : "Escribe una nota..."}
          value={noteText}
          required
          onChange={(e) => setNoteText(e.target.value)}
          rows={3}
          className="w-full text-xs bg-[var(--bg3)] border border-[var(--b1)] rounded-lg px-2.5 py-2 text-[var(--text)] placeholder-[var(--text-dim)] focus:border-accent outline-none resize-none min-h-[70px]"
        />

        {/* Color Presets and Submit */}
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-1.5">
            {PRESET_COLORS.map((c) => (
              <button
                key={c.hex}
                type="button"
                onClick={() => setSelectedColor(c.hex)}
                style={{ backgroundColor: c.hex }}
                className={`w-[18px] h-[18px] rounded-full flex items-center justify-center cursor-pointer transition-all ${
                  selectedColor === c.hex ? "ring-2 ring-[var(--text)]/50 scale-110 shadow-sm" : "opacity-85 hover:opacity-100 hover:scale-105"
                }`}
                title={c.name}
              >
                {selectedColor === c.hex && (
                  <Check size={10} className="text-white stroke-[3.5]" />
                )}
              </button>
            ))}
            {!showTitleField && (
              <button
                type="button"
                onClick={() => setShowTitleField(true)}
                className="text-[11px] font-bold text-[var(--text-muted)] hover:text-[var(--text)] transition-colors cursor-pointer ml-1"
              >
                + Título
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={!noteText.trim()}
            className="flex items-center gap-1 px-3 py-1.5 bg-accent text-white rounded-lg text-[11px] font-bold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-md"
          >
            <Plus size={11} className="stroke-[2.5]" />
            <span>{isEn ? "Add note" : "Agregar nota"}</span>
          </button>
        </div>
      </form>

      {/* Notes List / Scroll Area */}
      <div className="flex-1 overflow-y-auto p-3.5 flex flex-col gap-2.5 min-h-0 select-text">
        {notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-[var(--text-dim)] gap-2.5">
            <FileText size={32} className="text-[var(--text-dim)] opacity-40 mb-1" />
            <p className="font-bold text-xs text-[var(--text)]">
              {isEn ? "No notes yet" : "Aún no agregaste notas"}
            </p>
            <p className="max-w-[190px] leading-relaxed text-[11px] text-[var(--text-dim)] opacity-85">
              {isEn
                ? "Drag a transcript segment here to auto-create a note"
                : "Escribí puntos clave durante la reunión."}
            </p>
          </div>
        ) : (
          notes.map((note) => {
            const isEditing = editingId === note.id;
            return (
              <div
                key={note.id}
                style={{ borderLeftColor: note.color }}
                className="bg-[var(--bg3)] border-l-4 border-t border-r border-b border-[var(--b1)] rounded-lg p-2.5 flex flex-col gap-1.5 relative group shadow-sm transition-all"
              >
                {isEditing ? (
                  /* Inline Edit Mode */
                  <div className="flex flex-col gap-1.5 w-full">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full text-xs font-semibold bg-[var(--bg2)] border border-[var(--b1)] rounded-lg px-2.5 py-1 text-[var(--text)] outline-none"
                    />
                    <textarea
                      value={editText}
                      required
                      onChange={(e) => setEditText(e.target.value)}
                      rows={2}
                      className="w-full text-xs bg-[var(--bg2)] border border-[var(--b1)] rounded-lg px-2.5 py-1.5 text-[var(--text)] outline-none resize-none"
                    />
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center gap-1.5">
                        {PRESET_COLORS.map((c) => (
                          <button
                            key={c.hex}
                            type="button"
                            onClick={() => setEditColor(c.hex)}
                            style={{ backgroundColor: c.hex }}
                            className={`w-[15px] h-[15px] rounded-full flex items-center justify-center cursor-pointer transition-all ${
                              editColor === c.hex ? "ring-2 ring-[var(--text)]/50 scale-110 shadow-sm" : "opacity-85 hover:opacity-100 hover:scale-105"
                            }`}
                            title={c.name}
                          >
                            {editColor === c.hex && (
                              <Check size={8} className="text-white stroke-[3.5]" />
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="px-2 py-0.5 border border-[var(--b1)] rounded-md text-[10px] text-[var(--text-dim)] hover:bg-[var(--surface-hover)] cursor-pointer"
                        >
                          {isEn ? "Cancel" : "Cancelar"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveEdit(note.id)}
                          className="flex items-center gap-1 px-2.5 py-0.5 bg-accent text-white rounded-md text-[10px] hover:brightness-110 cursor-pointer font-bold"
                        >
                          <Save size={10} />
                          <span>{isEn ? "Save" : "Guardar"}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Display Mode */
                  <>
                    <div className="flex items-start justify-between">
                      <span className="text-[9px] font-semibold text-[var(--text-dim)] font-mono">
                        {note.timestamp}
                      </span>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center gap-1">
                        <button
                          onClick={() => handleStartEdit(note)}
                          className="p-0.5 text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)] rounded transition-colors cursor-pointer"
                          title={isEn ? "Edit note" : "Editar nota"}
                        >
                          <Edit3 size={11} />
                        </button>
                        <button
                          onClick={() => onDeleteNote(note.id)}
                          className="p-0.5 text-[var(--text-dim)] hover:text-red-400 hover:bg-red-500/10 rounded transition-colors cursor-pointer"
                          title={isEn ? "Delete note" : "Eliminar nota"}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                    {note.title && (
                      <h4 className="text-[11px] font-bold text-[var(--text)] leading-tight">
                        {note.title}
                      </h4>
                    )}
                    <p className="text-[11px] text-[var(--text-muted)] leading-relaxed whitespace-pre-wrap select-text">
                      {note.text}
                    </p>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer: Export & Notes Count */}
      <div className="bg-[var(--bg3)] border-t border-[var(--b1)] p-2.5 px-3.5 flex items-center justify-between flex-shrink-0 text-xs font-semibold text-[var(--text-dim)]">
        <button
          type="button"
          onClick={onExportNotes}
          disabled={notes.length === 0}
          className="flex items-center gap-1.5 text-[var(--text-dim)] hover:text-[var(--text)] disabled:opacity-30 disabled:hover:text-[var(--text-dim)] disabled:cursor-not-allowed cursor-pointer transition-colors"
        >
          <Download size={13} className="stroke-[2]" />
          <span>{isEn ? "Export notes" : "Exportar notas"}</span>
        </button>
        <span className="font-medium text-[var(--text-dim)]">
          {notes.length} {notes.length === 1 ? (isEn ? "note" : "nota") : (isEn ? "notes" : "notas")}
        </span>
      </div>
    </div>
  );
};
